import {
  WS_CLOSE,
  wsServerFrameSchema,
  type ResyncReason,
  type WsEvent,
  type WsServerFrame,
} from '@chatapp-inbox/shared';

/**
 * Estado da conexão em tempo real, do ponto de vista de quem usa a tela:
 * - `connecting`: primeira conexão desta sessão ainda não completou;
 * - `live`: recebendo eventos;
 * - `reconnecting`: caiu (rede, servidor reiniciando) e está tentando de novo com espera crescente;
 * - `paused`: o servidor substituiu esta conexão por outra do mesmo usuário (abas demais); só volta
 *   por pedido da pessoa, para as abas não ficarem se derrubando em laço.
 */
export type SocketStatus = 'connecting' | 'live' | 'reconnecting' | 'paused';

export type ReadyFrame = Extract<WsServerFrame, { type: 'ready' }>;

export interface RealtimeHandlers {
  onStatus: (status: SocketStatus) => void;
  /**
   * Uma tentativa de conexão começou (a primeira e cada nova). `onStatus` só avisa quando o estado
   * MUDA, e quem precisa saber desde quando a tela pode estar sem eventos conta a partir daqui.
   */
  onAttempt?: () => void;
  onReady: (frame: ReadyFrame) => void;
  onEvent: (event: WsEvent, meta: { id: number; at: string }) => void;
  /** O servidor não consegue retomar de onde parei: recarregue o que está na tela pela API HTTP. */
  onResync: (reason: ResyncReason) => void;
  /**
   * O servidor encerrou a sessão desta conexão (4401). Confira se ela ainda vale: se não vale, o
   * pedido protegido dá 401 e o app inteiro vai para o login (esta classe é parada junto). Se
   * vale (ex.: a revalidação do servidor foi precoce), a conexão simplesmente recomeça.
   */
  onSessionEnded: () => Promise<void>;
}

export interface RealtimeSocketOptions {
  /** Endereço do WebSocket SEM query (ws:// ou wss://). */
  url: () => string;
  WebSocketImpl?: typeof WebSocket;
  random?: () => number;
  now?: () => number;
}

const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
/** Depois disto conectado, a conexão conta como estável e a espera volta ao início. */
const STABLE_AFTER_MS = 10_000;
const CONNECT_TIMEOUT_MS = 15_000;
const PING_EVERY_MS = 20_000;
/** O servidor responde cada ping; sem NENHUM quadro por tanto tempo, a conexão morreu em silêncio. */
const SILENCE_LIMIT_MS = 50_000;
const WATCHDOG_EVERY_MS = 10_000;
/** Código de fechamento usado por nós quando abandonamos uma conexão muda. */
const CLOSE_ABANDONED = 4000;

/**
 * Cliente do WebSocket da caixa de entrada: conecta, mantém a conexão viva (ping + vigia de
 * silêncio), reconecta com espera crescente e RETOMA de onde parou (`lastEventId`), de modo que
 * uma queda curta não perde nada. Não conhece React nem o cache: só entrega quadros validados.
 */
export class RealtimeSocket {
  private socket: WebSocket | null = null;
  private lastEventId: number | null = null;
  private attempt = 0;
  private stopped = true;
  private everLive = false;
  private lastFrameAt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  /** `null` até o primeiro anúncio: o estado inicial também precisa ser dito a quem escuta. */
  private status: SocketStatus | null = null;

  constructor(
    private readonly options: RealtimeSocketOptions,
    private readonly handlers: RealtimeHandlers,
  ) {}

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    window.addEventListener('online', this.onNudge);
    document.addEventListener('visibilitychange', this.onNudge);
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    window.removeEventListener('online', this.onNudge);
    document.removeEventListener('visibilitychange', this.onNudge);
    this.clearTimers();
    this.detach();
  }

  /** "Reconectar" depois de `paused`. */
  resume(): void {
    if (this.stopped || this.status !== 'paused') return;
    this.attempt = 0;
    this.connect();
  }

  // ------------------------------------------------------------------ conexão

  private setStatus(status: SocketStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.handlers.onStatus(status);
  }

  private urlWithResume(): string {
    const url = new URL(this.options.url());
    if (this.lastEventId !== null) url.searchParams.set('lastEventId', String(this.lastEventId));
    return url.toString();
  }

  private connect(): void {
    if (this.stopped) return;
    this.clearTimers();
    this.detach();
    this.setStatus(
      this.everLive ? 'reconnecting' : this.attempt > 0 ? 'reconnecting' : 'connecting',
    );

    this.handlers.onAttempt?.();
    const Impl = this.options.WebSocketImpl ?? WebSocket;
    let socket: WebSocket;
    try {
      socket = new Impl(this.urlWithResume());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;
    this.lastFrameAt = this.now();
    socket.addEventListener('message', this.onMessage);
    socket.addEventListener('close', this.onClose);
    socket.addEventListener('error', this.onError);

    // Sem `ready` a tempo (rede que aceita e não responde), abandona e tenta de novo.
    this.connectTimer = setTimeout(() => this.abandon(), CONNECT_TIMEOUT_MS);
  }

  private readonly onMessage = (event: MessageEvent): void => {
    this.lastFrameAt = this.now();
    if (typeof event.data !== 'string') return;
    let json: unknown;
    try {
      json = JSON.parse(event.data);
    } catch {
      return;
    }
    const parsed = wsServerFrameSchema.safeParse(json);
    // Quadro desconhecido (servidor mais novo que a interface): ignora, não derruba a conexão.
    if (!parsed.success) return;
    const frame = parsed.data;
    switch (frame.type) {
      case 'ready':
        this.onReady(frame);
        break;
      case 'event':
        // Eventos repetidos (retomada que se sobrepõe ao que já chegou) não se aplicam duas vezes.
        if (this.lastEventId !== null && frame.id <= this.lastEventId) break;
        this.lastEventId = frame.id;
        this.handlers.onEvent(frame.event, { id: frame.id, at: frame.at });
        break;
      case 'resync':
        this.lastEventId = frame.head;
        this.handlers.onResync(frame.reason);
        break;
      case 'pong':
        break;
    }
  };

  private onReady(frame: ReadyFrame): void {
    if (this.connectTimer) clearTimeout(this.connectTimer);
    this.connectTimer = null;
    // Primeira conexão (ao vivo): o ponto de partida é o cabeçalho do log. Nas seguintes
    // `lastEventId` já existe e é ele que manda (a retomada).
    this.lastEventId ??= frame.head;
    this.everLive = true;
    this.setStatus('live');
    this.handlers.onReady(frame);
    this.stableTimer = setTimeout(() => {
      this.attempt = 0;
    }, STABLE_AFTER_MS);
    this.pingTimer = setInterval(() => this.ping(), PING_EVERY_MS);
    this.watchdogTimer = setInterval(() => {
      if (this.now() - this.lastFrameAt > SILENCE_LIMIT_MS) this.abandon();
    }, WATCHDOG_EVERY_MS);
  }

  private ping(): void {
    if (this.socket?.readyState !== 1) return;
    try {
      this.socket.send(JSON.stringify({ type: 'ping' }));
    } catch {
      /* o vigia de silêncio trata */
    }
  }

  /** O `close` que vem junto de um erro cuida da reconexão. */
  private readonly onError = (): void => {};

  private readonly onClose = (event: CloseEvent): void => {
    this.clearTimers();
    this.socket = null;
    if (this.stopped) return;
    if (event.code === WS_CLOSE.replaced) {
      this.setStatus('paused');
      return;
    }
    if (event.code === WS_CLOSE.sessionEnded) {
      this.setStatus('reconnecting');
      // Se a sessão acabou de verdade, o pedido protegido dá 401, o app vai para o login e a
      // chamada a `stop()` torna `scheduleReconnect` inofensivo.
      this.handlers.onSessionEnded().then(
        () => this.scheduleReconnect(),
        () => this.scheduleReconnect(),
      );
      return;
    }
    this.scheduleReconnect();
  };

  /** Conexão muda: solta e recomeça sem esperar o navegador perceber o fechamento. */
  private abandon(): void {
    if (this.stopped) return;
    this.detach();
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    this.clearTimers();
    this.setStatus('reconnecting');
    const exponential = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** this.attempt);
    const jitter = 0.75 + 0.5 * (this.options.random ?? Math.random)();
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => this.connect(), exponential * jitter);
  }

  /** Voltou a rede ou a aba: se está esperando para reconectar, tenta já. */
  private readonly onNudge = (): void => {
    if (this.stopped || !this.reconnectTimer) return;
    if (document.visibilityState === 'hidden') return;
    this.attempt = 0;
    this.connect();
  };

  // ------------------------------------------------------------------ utilitários

  private now(): number {
    return (this.options.now ?? Date.now)();
  }

  private clearTimers(): void {
    for (const timer of [this.reconnectTimer, this.connectTimer, this.stableTimer]) {
      if (timer) clearTimeout(timer);
    }
    for (const timer of [this.pingTimer, this.watchdogTimer]) {
      if (timer) clearInterval(timer);
    }
    this.reconnectTimer = this.connectTimer = this.stableTimer = null;
    this.pingTimer = this.watchdogTimer = null;
  }

  private detach(): void {
    const socket = this.socket;
    if (!socket) return;
    this.socket = null;
    socket.removeEventListener('message', this.onMessage);
    socket.removeEventListener('close', this.onClose);
    socket.removeEventListener('error', this.onError);
    try {
      socket.close(CLOSE_ABANDONED);
    } catch {
      /* já estava fechado */
    }
  }
}
